import type { ProfileCategoryCountsResponse, ProfileResponse, ProfileSort } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

/** Rows per page, matching the original `useInfiniteApi('/profiles', 25)` default. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Redux state for the client HNWIs list. Server-side pagination + filtering: the
 * slice holds the current page's rows, the grand `total`, the load lifecycle
 * (`error` is a banner message), the current page / pageSize (`page` is 0-indexed)
 * and the pruned `filters` that drive the fetch. Filter-bar edit state, quick
 * filters, column config and row selection stay local to the page (view state);
 * every field here is serialisable.
 */
/**
 * The last "Ask AI" request (#567).
 *
 * The answer's rules are not applied here: the rule tree is the page's local
 * edit state, so the page picks `answer` up and loads it. `answerId` changes
 * with every answer, so asking the same question twice still applies it twice.
 */
export interface AiSearchState {
  status: 'idle' | 'asking' | 'failed';
  error: string | null;
  answer: { filters: FilterGroup; interpretation: string; query: string } | null;
  answerId: number;
}

export interface MyProfilesState {
  rows: ProfileResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  filters: FilterGroup | null;
  /**
   * The keyword search (#567), ANDed with `filters` by the server. Kept apart
   * from the rule tree on purpose: a saved view stores rules, and a word typed
   * to find one person is not part of any view.
   */
  search: string;
  /** The column the whole list is ordered by on the server; null = by name (#714). */
  sort: ProfileSort | null;
  aiSearch: AiSearchState;
  /**
   * The category band's figures (#553), or `null` until the aggregate fetch
   * resolves; the tiles then show their label alone rather than a zero that
   * would read as a real count.
   *
   * A separate fetch from the rows, and deliberately not recomputed under a
   * filter: the band says how many there ARE, the table's own badge says how
   * many you are looking at. Recounting here would make clicking a figure
   * produce a different figure.
   */
  categoryCounts: ProfileCategoryCountsResponse | null;
  /**
   * The licence the list is held to (PI-117): the signed-in user's own. `null`
   * until the page sets it, and no page is fetched before; for an admin, a
   * request without it would return every licence.
   */
  licenceId: string | null;
  /**
   * The choices of the Country and Custom tags facets (#615), or `null` until
   * they land. Fetched apart from the rows: they describe the licence, not the
   * current search, so they do not move when a filter is added.
   */
  facetOptions: FacetOptions;
}

export interface FacetOptions {
  countries: string[] | null;
  tags: string[] | null;
}

export const initialState: MyProfilesState = {
  rows: [],
  total: 0,
  loading: false,
  error: null,
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: null,
  search: '',
  sort: null,
  aiSearch: { status: 'idle', error: null, answer: null, answerId: 0 },
  categoryCounts: null,
  licenceId: null,
  facetOptions: { countries: null, tags: null },
};
